import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CONFIG, IMAGES } from "../../lib/config";
import Countdown from "./Countdown";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="mask-line">
    <motion.span
      variants={lineVariants}
      custom={i}
      initial="hidden"
      animate="visible"
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] w-full items-end overflow-hidden bg-[#09090b]"
    >
      {/* Parallax eclipse background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.heroEclipse}
          alt="Éclipse"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#09090b]" />
        <div className="eclipse-ring absolute left-1/2 top-1/3 h-[70vw] w-[70vw] -translate-x-1/2 -translate-y-1/2" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-16 pt-28 lg:px-10 lg:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono-x text-[11px] uppercase tracking-[0.3em] text-zinc-400"
        >
          <span className="text-[#FF0000]">AKTRA</span>
          <span className="h-px w-8 bg-zinc-600" />
          <span>{CONFIG.location}</span>
          <span className="h-px w-8 bg-zinc-600" />
          <span>{CONFIG.tagline}</span>
        </motion.div>

        <h1 className="font-display text-[22vw] leading-[0.82] tracking-tighter text-white sm:text-[20vw] lg:text-[16vw]">
          <MaskLine i={0}>ÉCLIPSE</MaskLine>
        </h1>

        <div className="mt-8 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="max-w-md text-base leading-relaxed text-zinc-300 lg:text-lg"
          >
            La nuit où l’été s’éteint. Le tout premier DJ set de{" "}
            <span className="text-white">Nokuji</span>, des artistes surprises, une seule
            règle&nbsp;: ne pas rater le moment.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="flex flex-col gap-5"
          >
            <Countdown />
            <div className="flex flex-wrap gap-3">
              <a
                href={CONFIG.weezeventUrl}
                data-testid="hero-buy-ticket"
                className="glow-red group flex items-center gap-2 bg-[#FF0000] px-8 py-4 font-mono-x text-sm font-semibold uppercase tracking-[0.15em] text-white hover:bg-[#ff2424]"
              >
                Acheter mon billet
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </a>
              <a
                href="#lemfi"
                data-testid="hero-lemfi-link"
                className="flex items-center gap-2 border border-white/20 bg-white/5 px-8 py-4 font-mono-x text-sm uppercase tracking-[0.15em] text-white backdrop-blur-md transition-colors duration-300 hover:border-[#FF0000] hover:text-[#FF0000]"
              >
                -30% avec LemFi
              </a>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        <span className="font-mono-x text-[10px] uppercase tracking-[0.3em] text-zinc-500">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-[#FF0000] to-transparent"
        />
      </motion.div>
    </section>
  );
}
